import React, { useState, ChangeEvent, useRef, useEffect, useMemo } from "react"
import { Camera, Plus, Trash2, Upload, X } from "lucide-react"
import GradientStrokeButton from "../../atoms/Utlis/GradientStrokeButton";
import GradientFillButton from "../../atoms/Utlis/GradientFillButton";
import { PreviewWebsite } from "../../molecules/Dashboard/PreviewWebsite/PreviewWebsite";
import { createStudioConfig, fetchStudioConfigAPI, mediaDeleteApi, mediaUploadAPI } from "../../../Redux/ApiCalls/Dashboard/StudioConfig";
import { Service, SocialMedia, StudioData } from "../../../Data/studio.dto";
import { stripCdn, toCdnUrl } from "../../../Utils/helper";
import LoadingDots from "../../atoms/Utlis/LoadinDots";


const UploadBox = ({
    label,
    multiple,
    file,
    files,
    onChange,
    onRemove,
    onRemoveAt,
    existing,
    onRemoveExisting,
}: {
    label: string;
    multiple?: boolean;
    file?: File;
    files?: File[];
    existing?: string[];
    onRemoveExisting?: (url: string) => void;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onRemove?: () => void;
    onRemoveAt?: (index: number) => void;
}) => {
    const [singlePreview, setSinglePreview] = useState<string | undefined>();
    const [previewList, setPreviewList] = useState<string[]>([]);

    // Single File Preview
    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setSinglePreview(url);
            return () => URL.revokeObjectURL(url);
        } else {
            setSinglePreview(undefined);
        }
    }, [file]);

    // Multiple Files Preview
    useEffect(() => {
        if (!files || files.length === 0) {
            setPreviewList([]);
            return;
        }

        const urls = files.map((f) => URL.createObjectURL(f));
        setPreviewList(urls);

        // return () => urls.forEach((u) => URL.revokeObjectURL(u));
    }, [files]);

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{label}</label>

            <div
                className={`relative rounded-xl p-[2px] transition hover:opacity-90 ${file || (files && files.length > 0)
                    ? "bg-gradient-to-r from-yellow-300 via-pink-300 to-pink-400"
                    : "bg-gray-200 hover:bg-gradient-to-r hover:from-yellow-100 hover:via-pink-100 hover:to-pink-200"
                    }`}
            >
                <div className="bg-white rounded-xl p-6 text-center">
                    <input
                        type="file"
                        accept="image/*"
                        multiple={multiple}
                        onChange={onChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    <div className="pointer-events-none text-gray-500 flex flex-col items-center gap-2">
                        <Upload className="w-6 h-6" />
                        <p className="text-sm font-medium">
                            {multiple ? "Click or drag to upload images" : "Click to upload image"}
                        </p>
                    </div>
                </div>
            </div>
            {existing && existing.length !== 0 && <div className="flex flex-wrap gap-3 mt-3">
                {existing.length > 1 ? existing.map((u, i) => (
                    <div key={i} className="relative">
                        <img src={toCdnUrl(u)} className="w-24 h-24 object-cover rounded-lg border" />
                        {onRemoveExisting && (
                            <button
                                onClick={() => onRemoveExisting?.(u)}
                                className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-100"
                            >
                                <X className="w-4 h-4 text-red-500" />
                            </button>
                        )}
                    </div>
                )) : <div className="relative mt-3 flex items-center justify-center">
                    <img
                        src={toCdnUrl(existing[0])}
                        className="w-auto h-auto max-h-[200px] object-cover rounded-lg border"
                    />
                    {onRemove && (
                        <button
                            onClick={onRemove}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-100"
                        >
                            <X className="w-4 h-4 text-red-500" />
                        </button>
                    )}
                </div>}

            </div>}
            {/* Single Preview */}
            {singlePreview && (
                <div className="relative mt-3 flex items-center justify-center">
                    <img
                        src={singlePreview}
                        className="w-auto h-auto max-h-[200px] object-cover rounded-lg border"
                    />
                    {onRemove && (
                        <button
                            onClick={onRemove}
                            className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-100"
                        >
                            <X className="w-4 h-4 text-red-500" />
                        </button>
                    )}
                </div>
            )}

            {/* Multi Preview */}
            {previewList.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                    {previewList.map((u, i) => (
                        <div key={i} className="relative">
                            <img src={u} className="w-24 h-24 object-cover rounded-lg border" />
                            {onRemoveAt && (
                                <button
                                    onClick={() => onRemoveAt(i)}
                                    className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-red-100"
                                >
                                    <X className="w-4 h-4 text-red-500" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface StudioWebsiteFormProps {
    studioConfig?: StudioData;
    isEdit?: boolean;
    setIsEdit?: (value: boolean) => void;
    setIsDataUpdate?: (value: boolean) => void;
}

const StudioWebsiteForm: React.FC<StudioWebsiteFormProps> = ({ studioConfig, isEdit = false, setIsEdit, setIsDataUpdate }) => {
    const [isLoading, setIsLoading] = useState(false)

    const [studioName, setStudioName] = useState(studioConfig?.name ?? "");

    const [studioSlug, setStudioSlug] = useState(studioConfig?.slug ?? "");

    const [studioDescription, setStudioDescription] = useState(studioConfig?.description ?? "");

    const [studioLogo, setStudioLogo] = useState<{ new?: File; old?: string }>({
        new: undefined,
        old: toCdnUrl(studioConfig?.logo),
    });

    const [studioHeroImages, setStudioHeroImages] = useState<{
        new?: File[];
        old: string[];
    }>({
        new: undefined,
        old: (studioConfig?.heroImages?.map(toCdnUrl).filter((url): url is string => typeof url === "string" && url !== undefined)) ?? [],
    });

    const [studioGalleryImages, setStudioGalleryImages] = useState<
        { tag: string; images: { new?: File; old?: string } }[]
    >(
        studioConfig?.galleryImages?.map((g) => ({
            tag: g.title,
            images: { new: undefined, old: toCdnUrl(g.images) },
        })) ?? []
    );

    const [studioSocialMedia, setStudioSocialMedia] = useState<SocialMedia>({
        facebook: studioConfig?.socialMedia?.facebook ?? "",
        instagram: studioConfig?.socialMedia?.instagram ?? "",
        youtube: studioConfig?.socialMedia?.youtube ?? "",
        portfolio: studioConfig?.socialMedia?.portfolio ?? "",
    });

    const [studioServices, setStudioServices] = useState<Service[]>(
        studioConfig?.services ?? [
            { title: "", description: "" }, // Default empty service if none exists
        ]
    );

    const [studioSections, setStudioSections] = useState<
        {
            id: string;
            heading: string;
            background: { new?: File; old?: string };
            samples: string[];
            newSamples?: File[];
        }[]
    >(
        studioConfig?.sections?.map((s) => ({
            id: s.id,
            heading: s.heading,
            background: { old: toCdnUrl(s.background) },
            samples: (s.samples?.map(toCdnUrl).filter((url): url is string => typeof url === "string" && url !== undefined)) ?? [],
            newSamples: [],
        })) ?? []
    );

    const [studioContact, setStudioContact] = useState<{
        phone: string;
        email: string;
        address: string;
    }>({
        phone: studioConfig?.contact?.phone ?? "",
        email: studioConfig?.contact?.email ?? "",
        address: studioConfig?.contact?.address ?? "",
    });

    const [showPreview, setShowPreview] = useState(false);

    const [previewObject, setPreviewObject] = useState<any>();

    const logoRef = useRef<HTMLInputElement>(null);

    const [errors, setErrors] = useState<{
        basic?: { studioName?: string; heroImages?: string; logo?: string; studioDescription?: string }
        gallery?: { tag?: string; image?: string }[]
        social?: { facebook?: string; insta?: string; yt?: string; portfolio?: string }
        services?: { title?: string; description?: string }[]
        sections?: { id?: string; heading?: string; background?: string; samples?: string }[]
        contact?: { phone?: string; email?: string; address?: string }
    }>({})

    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>();
    const [removedUrl, setRemovedUrl] = useState<string[]>([])

    useEffect(() => {
        if (studioLogo.new) {
            const url = URL.createObjectURL(studioLogo.new);
            setLogoPreviewUrl(url);

            return () => URL.revokeObjectURL(url); // cleanup ONLY when file changes
        }
    }, [studioLogo.new]);

    // Debounce studio name to slug conversion
    useEffect(() => {
        const handler = setTimeout(() => {
            const next = createSlug(studioName)
            setErrors({ ...errors, basic: { ...errors.basic, studioName: undefined } })
            setStudioSlug(next)
        }, 400)
        return () => clearTimeout(handler)
    }, [studioName])

    useEffect(() => {
        const handler = setTimeout(() => {
            if (studioSlug && (isEdit && studioConfig?.slug !== studioSlug)) {
                setIsLoading(true)
                fetchStudioConfigAPI(studioSlug).then((data: any) => {
                    if (data.data) {
                        setErrors({ ...errors, basic: { ...errors.basic, studioName: "Studio name is already taken." } })
                    }
                }).catch(err => {
                    console.log(err)
                }).finally(() => {
                    setIsLoading(false)
                })
            }
        }, 3000)
        return () => clearTimeout(handler)
    }, [studioSlug])



    const isValidUrl = (value: string | undefined) => {
        if (!value) return true
        try {
            // Ensure protocol; if missing, assume invalid
            const withProto = /^(https?:)?\/\//i.test(value) ? value : `https://${value}`
            // eslint-disable-next-line no-new
            new URL(withProto)
            return true
        } catch {
            return false
        }
    }

    const validate = () => {
        const newErrors: typeof errors = {}

        // Basic
        const basic: NonNullable<typeof newErrors.basic> = {}
        if (!studioName.trim()) basic.studioName = "Studio name is required"
        if (!studioDescription.trim()) basic.studioDescription = "Studio description is required"
        const heroCount = (studioHeroImages.old?.length || 0) + (studioHeroImages.new?.length || 0)
        if (heroCount === 0) basic.heroImages = "Add at least one hero image"
        if (Object.keys(basic).length) newErrors.basic = basic

        // Gallery
        if (studioGalleryImages.length) {
            newErrors.gallery = studioGalleryImages.map((g) => {
                const ge: { tag?: string; image?: string } = {}
                if (!g.tag?.trim()) ge.tag = "Tag/title is required"
                const hasImage = Boolean(g.images.old || g.images.new)
                if (!hasImage) ge.image = "Image is required"
                return ge
            })
        }

        // Social (validate only if provided)
        const social: NonNullable<typeof newErrors.social> = {}
        if (studioSocialMedia?.facebook && !isValidUrl(studioSocialMedia.facebook)) social.facebook = "Invalid URL"
        if (studioSocialMedia?.instagram && !isValidUrl(studioSocialMedia.instagram)) social.insta = "Invalid URL"
        if (studioSocialMedia?.youtube && !isValidUrl(studioSocialMedia.youtube)) social.yt = "Invalid URL"
        if (studioSocialMedia?.portfolio && !isValidUrl(studioSocialMedia.portfolio)) social.portfolio = "Invalid URL"
        if (Object.keys(social).length) newErrors.social = social

        // Services
        if (studioServices.length) {
            newErrors.services = studioServices.map((s) => {
                const se: { title?: string; description?: string } = {}
                if (!s.title?.trim()) se.title = "Title is required"
                if (!s.description?.trim()) se.description = "Description is required"
                return se
            })
        }

        // Sections
        if (studioSections.length) {
            newErrors.sections = studioSections.map((s) => {
                const se: { id?: string; heading?: string; background?: string; samples?: string } = {}
                if (!s.id?.trim()) se.id = "Section ID is required"
                if (!s.heading?.trim()) se.heading = "Heading is required"
                const hasBg = Boolean(s.background.old || s.background.new)
                if (!hasBg) se.background = "Background image is required"
                const samplesCount = (s.samples?.length || 0) + (s.newSamples?.length || 0)
                if (samplesCount === 0) se.samples = "Add at least one sample image"
                return se
            })
        }

        // Contact
        const contact: NonNullable<typeof newErrors.contact> = {}
        if (!studioContact.phone?.trim()) contact.phone = "Phone is required"
        else if (!/^\d{10}$/.test(studioContact.phone)) contact.phone = "Enter 10-digit phone number"
        if (!studioContact.email?.trim()) contact.email = "Email is required"
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studioContact.email)) contact.email = "Invalid email"
        if (!studioContact.address?.trim()) contact.address = "Address is required"
        if (Object.keys(contact).length) newErrors.contact = contact

        setErrors(newErrors)
        // If any section has any error
        const hasAnyError = Object.values(newErrors).some((val) => {
            if (Array.isArray(val)) return val.some((o) => Object.keys(o || {}).length)
            return val && Object.keys(val).length > 0
        })
        return !hasAnyError
    }



    // --- HANDLERS (no logic changes) ---
    const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            if (studioLogo.old) {
                setRemovedUrl([...removedUrl, studioLogo.old]);
            }
            setStudioLogo((pre) => {
                return {
                    new: e.target.files?.[0],
                    old: undefined,
                }
            })
        }
    }

    const handleHeroImagesChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files)
            setStudioHeroImages({
                new: [...(studioHeroImages.new || []), ...files],
                old: studioHeroImages.old,
            })
        }
    }

    const addGalleryImage = () => {
        setStudioGalleryImages([
            ...studioGalleryImages,
            { tag: "", images: { new: undefined, old: undefined } },
        ])
    }

    const updateGalleryImage = (index: number, field: string, value: any) => {
        const updated = [...studioGalleryImages]
        if (field === "tag") {
            updated[index].tag = value
        } else if (field === "image") {
            updated[index].images = { new: value, old: undefined }
        }
        else if (field === "oldImage") {
            updated[index].images = { new: undefined, old: undefined }
        }
        setStudioGalleryImages(updated)
    }

    const removeGalleryImage = (index: number) => {
        setStudioGalleryImages(studioGalleryImages.filter((_, i) => i !== index))
    }

    const addService = () => {
        setStudioServices([
            ...studioServices,
            { title: "", description: "" }
        ])
    }

    const updateService = (index: number, field: string, value: string) => {
        const updated = [...studioServices]
        updated[index][field as keyof (typeof updated)[0]] = value
        setStudioServices(updated)
    }

    const removeService = (index: number) => {
        setStudioServices(studioServices.filter((_, i) => i !== index))
    }

    const addSection = () => {
        setStudioSections([
            ...studioSections,
            {
                id: "",
                heading: "",
                background: { new: undefined, old: undefined },
                samples: [],
                newSamples: [],
            },
        ])
    }

    const updateSection = (index: number, field: string, value: any) => {
        const updated = [...studioSections]
        if (field === "background") {
            updated[index].background = {
                new: value,
                old: undefined,
            }
        } else if (field === "newSamples") {
            updated[index].newSamples = value
        } else {
            updated[index][field as keyof (typeof updated)[0]] = value
        }
        setStudioSections(updated)
    }

    const removeSection = (index: number) => {
        setStudioSections(studioSections.filter((_, i) => i !== index))
    }

    const createSlug = (name: string): string => {
        return name
            .toLowerCase() // convert to lowercase
            .trim() // remove leading/trailing spaces
            .replace(/[^\w\s-]/g, "") // remove non-word characters except spaces/hyphens
            .replace(/\s+/g, "-") // replace spaces with hyphens
            .replace(/--+/g, "-"); // replace multiple hyphens with single one
    };

    // --- Reusable UploadBox UI ---


    const handleShowPreview = () => {
        if (!validate()) {
            setShowPreview(false)
            return
        }
        const studioData = {
            name: studioName,
            slug: studioSlug,
            logo: studioLogo.new ? URL.createObjectURL(studioLogo.new) : studioLogo.old,
            description: studioDescription,
            heroImages: studioHeroImages.old || studioHeroImages.new
                ? [
                    ...(studioHeroImages.old || []),
                    ...(studioHeroImages.new?.map((f) => URL.createObjectURL(f)) || []),
                ]
                : [],
            galleryImages: studioGalleryImages.map((item) => ({
                images: item.images.old || (item.images.new ? URL.createObjectURL(item.images.new) : ""),
                title: item.tag,
            })),
            socialMedia: {
                facebook: studioSocialMedia?.facebook || "",
                instagram: studioSocialMedia?.instagram || "",
                youtube: studioSocialMedia?.youtube || "",
                portfolio: studioSocialMedia?.portfolio || "",
            },
            services: studioServices.map((item) => ({
                title: item.title,
                description: item.description,
                icon: "Camera", // default icon
            })),
            sections: studioSections.map((item) => ({
                id: item.id,
                heading: item.heading,
                background: item.background.old || (item.background.new ? URL.createObjectURL(item.background.new) : ""),
                samples: item.newSamples?.map((f) => URL.createObjectURL(f)) || item.samples || [],
            })),
            contact: { ...studioContact },
        };
        setPreviewObject(studioData)
        setShowPreview(true);
        console.log(studioData);

    };

    const previewElement = useMemo(() => {
        if (!previewObject || !showPreview) return null
        return <PreviewWebsite data={previewObject} />
    }, [previewObject, showPreview])

    const handleSubmit = async () => {
        if (!validate()) return

        const uploadedPaths: string[] = [] // Track newly uploaded files

        const safeUpload = async (file: File | null | undefined) => {
            if (!file) return undefined
            const res = await mediaUploadAPI(file)
            if (res?.path) {
                uploadedPaths.push(res.path) // keep track
                return res.path
            }
            return undefined
        }

        try {
            setIsLoading(true)

            // 1) Logo
            let logoUrl: string | undefined = studioLogo.old
            if (studioLogo.new) {
                logoUrl = await safeUpload(studioLogo.new)
            }

            // 2) Hero Images
            const heroNewFiles = studioHeroImages.new || []
            const uploadedHeroUrls = (
                await Promise.all(heroNewFiles.map((file) => safeUpload(file)))
            ).filter(Boolean) as string[]

            const heroUrls = [
                ...(studioHeroImages.old || []),
                ...uploadedHeroUrls,
            ]

            // 3) Gallery Images
            const galleryMapped = await Promise.all(
                studioGalleryImages.map(async (g) => {
                    let imageUrl = g.images.old
                    if (g.images.new) {
                        const uploaded = await safeUpload(g.images.new)
                        if (uploaded) imageUrl = uploaded
                    }
                    return {
                        images: imageUrl || "",
                        title: g.tag,
                    }
                })
            )

            // 4) Sections
            const sectionsMapped = await Promise.all(
                studioSections.map(async (s) => {
                    let backgroundUrl = s.background.old
                    if (s.background.new) {
                        const uploaded = await safeUpload(s.background.new)
                        if (uploaded) backgroundUrl = uploaded
                    }

                    const uploadedSamples = (
                        await Promise.all((s.newSamples || []).map((file) => safeUpload(file)))
                    ).filter(Boolean) as string[]

                    const samples = [
                        ...(s.samples || []),
                        ...uploadedSamples,
                    ]

                    return {
                        id: s.id,
                        heading: s.heading,
                        background: backgroundUrl || "",
                        samples,
                    }
                })
            )

            // Build final object
            const studioData = {
                name: studioName,
                slug: studioSlug,
                logo: logoUrl || "",
                description: studioDescription,
                heroImages: heroUrls,
                galleryImages: galleryMapped,
                socialMedia: {
                    facebook: studioSocialMedia?.facebook || "",
                    instagram: studioSocialMedia?.instagram || "",
                    youtube: studioSocialMedia?.youtube || "",
                    portfolio: studioSocialMedia?.portfolio || "",
                },
                services: studioServices.map((item) => ({
                    title: item.title,
                    description: item.description,
                    icon: "Camera",
                })),
                sections: sectionsMapped,
                contact: { ...studioContact },
            }

            setPreviewObject(studioData)
            console.log("studioData", studioData)
            await createStudioConfig(studioData);
            setShowPreview(true)
            if (removedUrl && removedUrl.length) {
                const uniquePaths = Array.from(new Set(removedUrl));
                await Promise.all(
                    uniquePaths.map(async (path) => {
                        const cleanPath = stripCdn(path);
                        try {
                            await mediaDeleteApi(cleanPath)
                        } catch (err) {
                            console.error("Rollback failed for path:", path, err)
                        }
                    })
                )

            }
            setIsEdit?.(false);
            setIsDataUpdate?.(true);
            console.log('called')
        } catch (error) {
            console.error("Submit Failed. Rolling back...", error)

            // 🔥 ROLLBACK
            await Promise.all(
                uploadedPaths.map(async (path) => {
                    try {
                        await mediaDeleteApi(path)
                    } catch (err) {
                        console.error("Rollback failed for path:", path, err)
                    }
                })
            )

        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-transparent py-8 px-4">
            <div className="max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex items-center gap-3 mb-8">
                        <Camera className="w-8 h-8 text-blue-600" />
                        <h1 className="text-3xl font-bold text-gray-800">
                            Studio Dashboard
                        </h1>
                    </div>

                    <div className="space-y-8">
                        {/* Basic Information */}
                        <section className="space-y-4">
                            <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">
                                Basic Information
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Studio Name
                                    </label>
                                    <input
                                        type="text"
                                        value={studioName}
                                        onChange={(e) => setStudioName(e.target.value)}
                                        className={`w-full px-4 py-2 border ${errors.basic?.studioName ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500`}
                                        placeholder="Studio Name"
                                    />
                                    {errors.basic?.studioName && (
                                        <p className="mt-1 text-sm text-red-600">{errors.basic.studioName}</p>
                                    )}
                                </div>



                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Studio url
                                    </label>
                                    <input
                                        type="text"
                                        value={studioSlug ? "https://kwikmemories.com/" + studioSlug : ""}
                                        disabled
                                        onChange={(e) => setStudioSlug(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="https://kwikmemories.com/studio-name"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Studio Description
                                </label>
                                <textarea
                                    value={studioDescription}
                                    onChange={(e) => setStudioDescription(e.target.value)}
                                    className={`w-full px-4 py-2 border ${errors.basic?.studioDescription ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500`}
                                    placeholder="A short description of your studio for SEO purposes."
                                    rows={3}
                                />
                                {errors.basic?.studioDescription && (
                                    <p className="mt-1 text-sm text-red-600">{errors.basic.studioDescription}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">This description will be used for SEO.</p>
                            </div>
                            {studioLogo.new ? (
                                <div className="flex flex-col gap-2 relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1 ">
                                        Studio Logo
                                    </label>
                                    <div className="relative rounded-lg border w-full flex items-center justify-center">
                                        <img
                                            src={logoPreviewUrl}
                                            alt="Studio Logo"
                                            className="w-auto h-auto max-h-[200px] object-contain "
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple={false}
                                            ref={logoRef}
                                            onChange={handleLogoChange}
                                            className="absolute inset-0 opacity-0 cursor-pointer hidden"
                                        />
                                        <GradientStrokeButton className="!absolute !top-[3%] !right-[1%] w-[100px] text-sm" onClick={() => { logoRef?.current?.click() }}>Change</GradientStrokeButton>
                                    </div>

                                </div>
                            ) : studioLogo.old ? (
                                <div className="flex flex-col gap-2 relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1 ">
                                        Studio Logo
                                    </label>
                                    <div className="relative rounded-lg border w-full flex items-center justify-center">
                                        <img
                                            src={studioLogo.old}
                                            alt="Studio Logo"
                                            className="w-auto h-auto max-h-[200px] object-contain "
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple={false}
                                            ref={logoRef}
                                            onChange={handleLogoChange}
                                            className="absolute inset-0 opacity-0 cursor-pointer hidden"
                                        />
                                        <GradientStrokeButton className="!absolute !top-[3%] !right-[1%] w-[100px] text-sm" onClick={() => { logoRef?.current?.click() }}>Change</GradientStrokeButton>
                                    </div>
                                </div>
                            ) : (
                                <UploadBox
                                    label="Studio Logo"
                                    file={studioLogo.new}
                                    onChange={handleLogoChange}
                                    onRemove={() => setStudioLogo({})}
                                />
                            )}
                            <UploadBox
                                label="Hero Images"
                                multiple
                                files={studioHeroImages.new}
                                onChange={handleHeroImagesChange}
                                existing={studioHeroImages.old}
                                onRemoveExisting={(url) => {
                                    setRemovedUrl([...removedUrl, url]);
                                    setStudioHeroImages({
                                        new: studioHeroImages.new,
                                        old: studioHeroImages.old?.filter((u) => u !== url),
                                    })
                                }}
                                onRemoveAt={(i) =>
                                    setStudioHeroImages({
                                        new: studioHeroImages.new?.filter((_, idx) => idx !== i),
                                        old: studioHeroImages.old,
                                    })
                                }
                            />
                            {errors.basic?.heroImages && (
                                <p className="mt-1 text-sm text-red-600">{errors.basic.heroImages}</p>
                            )}
                        </section>

                        {/* Gallery Images */}
                        <section className="space-y-4">
                            <div className="flex items-center justify-between   border-b  pb-2">
                                <h2 className="text-xl font-semibold text-gray-700 flex-1">
                                    Gallery Images
                                </h2>
                                <GradientFillButton onClick={addGalleryImage} icon={<Plus className="w-4 h-4" />}>
                                    Add
                                </GradientFillButton>
                            </div>
                            <div className="w-full flex flex-wrap justify-between gap-y-4">
                                {studioGalleryImages.map((item, index) => (
                                    <div
                                        key={index}
                                        className="p-4 border border-gray-200 rounded-lg bg-gray-50 w-[48%] "
                                    >
                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1 space-y-3">
                                                <input
                                                    type="text"
                                                    value={item.tag}
                                                    onChange={(e) =>
                                                        updateGalleryImage(index, "tag", e.target.value)
                                                    }
                                                    placeholder="Image tag/title"
                                                    className={`w-full px-4 py-2 border ${errors.gallery?.[index]?.tag ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                                />
                                                {errors.gallery?.[index]?.tag && (
                                                    <p className="text-sm text-red-600">{errors.gallery[index]?.tag}</p>
                                                )}
                                                <UploadBox
                                                    label="Upload Gallery Image"
                                                    file={item.images.new}
                                                    existing={item.images.old ? [item.images.old] : []}
                                                    onRemoveExisting={(url) => {
                                                        setRemovedUrl([...removedUrl, url]);
                                                        updateGalleryImage(index, "oldImage", undefined)
                                                    }}
                                                    onChange={(e) =>
                                                        e.target.files &&
                                                        updateGalleryImage(index, "image", e.target.files[0])
                                                    }
                                                    onRemove={() =>
                                                        updateGalleryImage(index, "image", undefined)
                                                    }
                                                />
                                                {errors.gallery?.[index]?.image && (
                                                    <p className="text-sm text-red-600">{errors.gallery[index]?.image}</p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeGalleryImage(index)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Social Media */}
                        <section className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-700 border-b pb-2 flex-1">
                                    Social Media
                                </h2>

                            </div>
                            <div className="flex w-full flex-wrap gap-4">
                                <div className="w-[48%]">
                                    <input
                                        type="text"
                                        value={studioSocialMedia?.facebook}
                                        onChange={(e) => {
                                            setStudioSocialMedia(prev => ({
                                                ...prev,
                                                facebook: e.target.value
                                            }));
                                        }}
                                        placeholder="Facebook link"
                                        className={`w-full px-4 py-2 border ${errors.social?.facebook ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.social?.facebook && (
                                        <p className="mt-1 text-sm text-red-600">{errors.social.facebook}</p>
                                    )}
                                </div>
                                <div className="w-[48%]">
                                    <input
                                        type="text"
                                        value={studioSocialMedia?.instagram}
                                        onChange={(e) => {
                                            setStudioSocialMedia(prev => ({
                                                ...prev,
                                                instagram: e.target.value
                                            }));
                                        }}
                                        placeholder="Instagram link"
                                        className={`w-full px-4 py-2 border ${errors.social?.insta ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.social?.insta && (
                                        <p className="mt-1 text-sm text-red-600">{errors.social.insta}</p>
                                    )}
                                </div>
                                <div className="w-[48%]">
                                    <input
                                        type="text"
                                        value={studioSocialMedia?.youtube}
                                        onChange={(e) => {
                                            setStudioSocialMedia(prev => ({
                                                ...prev,
                                                yt: e.target.value
                                            }));
                                        }}
                                        placeholder="Youtube link"
                                        className={`w-full px-4 py-2 border ${errors.social?.yt ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.social?.yt && (
                                        <p className="mt-1 text-sm text-red-600">{errors.social.yt}</p>
                                    )}
                                </div>
                                <div className="w-[48%]">
                                    <input
                                        type="text"
                                        value={studioSocialMedia?.portfolio}
                                        onChange={(e) => {
                                            setStudioSocialMedia(prev => ({
                                                ...prev,
                                                portfolio: e.target.value
                                            }));
                                        }}
                                        placeholder="Portfolio link"
                                        className={`w-full px-4 py-2 border ${errors.social?.portfolio ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.social?.portfolio && (
                                        <p className="mt-1 text-sm text-red-600">{errors.social.portfolio}</p>
                                    )}
                                </div>
                            </div>

                        </section>

                        {/* Services */}
                        <section className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <h2 className="text-xl font-semibold text-gray-700  flex-1">
                                    Services
                                </h2>
                                <GradientFillButton onClick={addService} icon={<Plus className="w-4 h-4" />}>
                                    Add
                                </GradientFillButton>
                            </div>

                            {studioServices.map((item, index) => (
                                <div
                                    key={index}
                                    className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                                >
                                    <div className="flex gap-4 items-start">
                                        <div className="flex-1 space-y-3">
                                            <input
                                                type="text"
                                                value={item.title}
                                                onChange={(e) =>
                                                    updateService(index, "title", e.target.value)
                                                }
                                                placeholder="Service title"
                                                className={`w-full px-4 py-2 border ${errors.services?.[index]?.title ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                            />
                                            {errors.services?.[index]?.title && (
                                                <p className="text-sm text-red-600">{errors.services[index]?.title}</p>
                                            )}
                                            <textarea
                                                value={item.description}
                                                onChange={(e) =>
                                                    updateService(index, "description", e.target.value)
                                                }
                                                placeholder="Service description"
                                                className={`w-full px-4 py-2 border ${errors.services?.[index]?.description ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                                rows={2}
                                            />
                                            {errors.services?.[index]?.description && (
                                                <p className="text-sm text-red-600">{errors.services[index]?.description}</p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeService(index)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </section>

                        {/* Sections */}
                        <section className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <h2 className="text-xl font-semibold text-gray-700  flex-1">
                                    Portfolio Sections
                                </h2>
                                <GradientFillButton onClick={addSection} icon={<Plus className="w-4 h-4" />}>
                                    Add
                                </GradientFillButton>
                            </div>

                            {studioSections.map((item, index) => (
                                <div
                                    key={index}
                                    className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                                >
                                    <div className="space-y-3">
                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1 space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <input
                                                        type="text"
                                                        value={item.id}
                                                        onChange={(e) =>
                                                            updateSection(index, "id", e.target.value)
                                                        }
                                                        placeholder="Section ID"
                                                        className={`px-4 py-2 border ${errors.sections?.[index]?.id ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                                    />
                                                    {errors.sections?.[index]?.id && (
                                                        <p className="text-sm text-red-600 col-span-1">{errors.sections[index]?.id}</p>
                                                    )}
                                                    <input
                                                        type="text"
                                                        value={item.heading}
                                                        onChange={(e) =>
                                                            updateSection(index, "heading", e.target.value)
                                                        }
                                                        placeholder="Section heading"
                                                        className={`px-4 py-2 border ${errors.sections?.[index]?.heading ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                                    />
                                                    {errors.sections?.[index]?.heading && (
                                                        <p className="text-sm text-red-600 col-span-1">{errors.sections[index]?.heading}</p>
                                                    )}
                                                </div>

                                                <UploadBox
                                                    label="Background Image"
                                                    file={item.background.new}
                                                    existing={item?.background?.old ? [item.background.old] : []}
                                                    onRemoveExisting={(url) => {
                                                        setRemovedUrl([...removedUrl, url])
                                                        updateSection(
                                                            index,
                                                            "background",
                                                            undefined
                                                        )
                                                    }}
                                                    onChange={(e) =>
                                                        e.target.files &&
                                                        updateSection(
                                                            index,
                                                            "background",
                                                            e.target.files[0]
                                                        )
                                                    }
                                                    onRemove={() =>
                                                        updateSection(index, "background", undefined)
                                                    }
                                                />
                                                {errors.sections?.[index]?.background && (
                                                    <p className="text-sm text-red-600">{errors.sections[index]?.background}</p>
                                                )}

                                                <UploadBox
                                                    label="Sample Images"
                                                    multiple
                                                    files={item.newSamples}
                                                    existing={item.samples}
                                                    onRemoveExisting={(url) => {
                                                        const updated = [...studioSections]
                                                        setRemovedUrl([...removedUrl, url]);
                                                        updated[index].samples = updated[index].samples?.filter(u => u !== url)
                                                        setStudioSections(updated)
                                                    }}
                                                    onChange={(e) =>
                                                        e.target.files &&
                                                        updateSection(
                                                            index,
                                                            "newSamples",
                                                            Array.from(e.target.files)
                                                        )
                                                    }
                                                    onRemoveAt={(i) =>
                                                        updateSection(
                                                            index,
                                                            "newSamples",
                                                            item.newSamples?.filter((_, idx) => idx !== i)
                                                        )
                                                    }
                                                />
                                                {errors.sections?.[index]?.samples && (
                                                    <p className="text-sm text-red-600">{errors.sections[index]?.samples}</p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeSection(index)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </section>

                        {/* Contact */}
                        <section className="space-y-4">
                            <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">
                                Contact Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Phone"
                                        value={studioContact.phone}
                                        onChange={(e) => {
                                            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10)
                                            setStudioContact({
                                                ...studioContact,
                                                phone: onlyDigits,
                                            })
                                        }}
                                        maxLength={10}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        className={`px-4 py-2 border ${errors.contact?.phone ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.contact?.phone && (
                                        <p className="text-sm text-red-600">{errors.contact.phone}</p>
                                    )}
                                </div>
                                <div>

                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={studioContact.email}
                                        onChange={(e) =>
                                            setStudioContact({
                                                ...studioContact,
                                                email: e.target.value,
                                            })
                                        }
                                        className={`px-4 py-2 border ${errors.contact?.email ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.contact?.email && (
                                        <p className="text-sm text-red-600">{errors.contact.email}</p>
                                    )}
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Address"
                                        value={studioContact.address}
                                        onChange={(e) =>
                                            setStudioContact({
                                                ...studioContact,
                                                address: e.target.value,
                                            })
                                        }
                                        className={`px-4 py-2 border ${errors.contact?.address ? "border-red-500" : "border-gray-300"} rounded-lg`}
                                    />
                                    {errors.contact?.address && (
                                        <p className="text-sm text-red-600">{errors.contact.address}</p>
                                    )}
                                </div>
                            </div>
                        </section>
                        <div className="w-full flex items-center justify-center gap-4">
                            {/* Submit */}
                            {isLoading ? <LoadingDots /> :
                                <>
                                    <GradientStrokeButton onClick={handleShowPreview} disabled={isLoading}>
                                        {showPreview ? "Update" : "Show"} Preview
                                    </GradientStrokeButton>
                                    <GradientStrokeButton onClick={handleSubmit} disabled={isLoading}>
                                        Submit
                                    </GradientStrokeButton>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
            {previewElement && (
                <div className="p-4 bg-white mt-4 relative">
                    <p className="w-full text-center text-xl font-semibold">Preview</p>
                    {previewElement}
                </div>
            )}
        </div>
    )
}

export default StudioWebsiteForm